import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Download, X, Share2, Smartphone, Chrome, Plus, MoreVertical, Bell, BellOff, BellRing } from 'lucide-react';
import { pushSupported, pushPermission, subscribeToPush, unsubscribeFromPush } from '../utils/push';

const HIDDEN_PREFIXES = ['/admin', '/login', '/register', '/forgot-password', '/reset-password', '/payment', '/unsubscribed'];

/**
 * "Add to Home Screen" (PWA install) button + instructions popup.
 *
 * Design goals:
 * - Everything stays INSIDE a single popup (no new tab / new page).
 * - Always shows BOTH iPhone and Android instructions (device is auto-selected but
 *   the user can switch tabs to see the other).
 * - Each step mimics the EXACT wording the browser uses ("Share" icon, "Add to Home Screen").
 * - If the browser fires `beforeinstallprompt` (Android Chrome / desktop Chrome / Edge),
 *   a native one-tap install button appears at the very top in addition to the steps.
 * - Auto-hides once the app is running as a standalone PWA.
 */
export default function InstallAppButton() {
  const location = useLocation();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [open, setOpen] = useState(false);
  const [installed, setInstalled] = useState(false);
  const [tab, setTab] = useState('ios');

  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const isIOS = useMemo(() => /iPad|iPhone|iPod/.test(ua) && !window.MSStream, [ua]);
  const isAndroid = useMemo(() => /Android/i.test(ua), [ua]);

  // Default the tab to the detected device
  useEffect(() => {
    if (isAndroid) setTab('android');
    else if (isIOS) setTab('ios');
    else setTab('desktop');
  }, [isAndroid, isIOS]);

  const hidden = HIDDEN_PREFIXES.some((p) => location.pathname === p || location.pathname.startsWith(p + '/'));
  const isStandalone = typeof window !== 'undefined' && (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator?.standalone === true
  );

  // Capture the install prompt
  useEffect(() => {
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
      setOpen(false);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  // Escape closes modal
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (hidden || installed || isStandalone) return null;

  const nativeInstall = async () => {
    if (!deferredPrompt) return;
    try {
      await deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice?.outcome === 'accepted') setInstalled(true);
      setDeferredPrompt(null);
      setOpen(false);
    } catch (e) {
      // ignore
    }
  };

  return (
    <>
      {/* Floating button — sits above Subscribe */}
      <button
        onClick={() => setOpen(true)}
        aria-label="Install Love Restored as an app"
        data-testid="install-app-btn"
        className="fixed z-40 rounded-full transition-all duration-300 hover:-translate-y-1 hover:scale-105 inline-flex items-center gap-2"
        style={{
          bottom: '86px',
          right: '24px',
          padding: '11px 18px',
          backgroundColor: '#2C2A29',
          color: '#FDFBF7',
          fontFamily: 'Manrope, sans-serif',
          fontSize: '11px',
          fontWeight: 800,
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          border: '1px solid rgba(228,194,101,0.5)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
        }}
      >
        <Smartphone className="w-4 h-4" strokeWidth={2.2} style={{ color: '#E8C265' }} />
        <span>Install App</span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
          style={{ background: 'rgba(44,42,41,0.5)', backdropFilter: 'blur(4px)' }}
          onClick={() => setOpen(false)}
          data-testid="install-modal"
        >
          <div className="w-full max-w-md max-h-[90vh] overflow-y-auto rounded-3xl" style={{ backgroundColor: '#FDFBF7', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }} onClick={(e) => e.stopPropagation()}>
            {/* Dark header */}
            <div className="p-6 relative" style={{ background: 'linear-gradient(135deg, #2C2A29 0%, #3F3B38 100%)', color: '#FDFBF7' }}>
              <button onClick={() => setOpen(false)} aria-label="Close" className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-white/10 transition" data-testid="install-close">
                <X className="w-4 h-4" />
              </button>
              <div className="flex items-center gap-2 mb-1.5">
                <Smartphone className="w-4 h-4" style={{ color: '#E8C265' }} />
                <p className="text-xs font-bold uppercase" style={{ letterSpacing: '0.12em', color: '#E8C265' }}>Love Restored on your home screen</p>
              </div>
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display, serif', lineHeight: 1.2 }}>
                Open it like an app, every time
              </h2>
              <p className="text-sm mt-2" style={{ color: '#E6D5B8', lineHeight: 1.6 }}>
                No app store. No download. It opens in its own window — fast, clean, and tucked next to your other favorites.
              </p>
            </div>

            <div className="p-6">
              {/* Native install (Android Chrome / desktop) — top of modal if available */}
              {deferredPrompt && (
                <button
                  onClick={nativeInstall}
                  className="w-full py-3 rounded-full text-sm font-bold inline-flex items-center justify-center gap-2 transition hover:-translate-y-0.5 mb-5"
                  style={{
                    background: 'linear-gradient(135deg, #F4D67A 0%, #E8C265 55%, #C99B2E 100%)',
                    color: '#3D2E0A',
                    fontFamily: 'Manrope, sans-serif',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    boxShadow: '0 6px 20px rgba(201,155,46,0.4)',
                    border: '1px solid rgba(255,255,255,0.5)',
                  }}
                  data-testid="install-native-btn"
                >
                  <Download className="w-4 h-4" /> One-tap install
                </button>
              )}

              {/* Tabs — iPhone / Android (so visitors can see both even if auto-detect misses) */}
              <div className="flex gap-2 mb-5 p-1 rounded-full" style={{ backgroundColor: '#F3EFE7' }}>
                <TabPill active={tab === 'ios'} onClick={() => setTab('ios')} testid="install-tab-ios">iPhone / iPad</TabPill>
                <TabPill active={tab === 'android'} onClick={() => setTab('android')} testid="install-tab-android">Android</TabPill>
                <TabPill active={tab === 'desktop'} onClick={() => setTab('desktop')} testid="install-tab-desktop">Computer</TabPill>
              </div>

              {tab === 'ios' && <IOSInstructions />}
              {tab === 'android' && <AndroidInstructions />}
              {tab === 'desktop' && <DesktopInstructions />}

              <p className="text-xs text-center mt-5 italic" style={{ color: '#8C867D', fontFamily: 'Nunito, sans-serif' }}>
                You can open this guide any time from the "Install App" button at the bottom of the page.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function TabPill({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      className="flex-1 px-3 py-1.5 rounded-full text-xs font-bold transition"
      style={{
        backgroundColor: active ? '#2C2A29' : 'transparent',
        color: active ? '#FDFBF7' : '#595553',
        fontFamily: 'Manrope, sans-serif',
      }}
      data-testid={testid}
    >
      {children}
    </button>
  );
}

function Step({ n, children }) {
  return (
    <li className="flex items-start gap-3 py-2">
      <span className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold" style={{ background: 'linear-gradient(135deg, #F4D67A 0%, #C99B2E 100%)', color: '#3D2E0A' }}>{n}</span>
      <span className="text-sm" style={{ fontFamily: 'Nunito, sans-serif', color: '#2C2A29', lineHeight: 1.65 }}>{children}</span>
    </li>
  );
}

function IOSInstructions() {
  return (
    <div data-testid="install-ios-steps">
      <p className="text-sm mb-3 flex items-center gap-2 font-semibold" style={{ fontFamily: 'Manrope, sans-serif', color: '#2C2A29' }}>
        <Smartphone className="w-4 h-4" style={{ color: '#B8891A' }} /> On iPhone or iPad (Safari)
      </p>
      <ol className="space-y-0.5 mb-3">
        <Step n={1}>
          Make sure you're in <strong>Safari</strong> (not Chrome). Look at the <strong>bottom toolbar</strong> for the <strong>Share icon</strong>{' '}
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-md mx-0.5 align-middle" style={{ backgroundColor: '#F3EFE7', border: '1px solid #E6E2D8' }}>
            <Share2 className="w-3.5 h-3.5" style={{ color: '#5B7C99' }} />
          </span>{' '}
          — it looks like a square with an arrow pointing up. Tap it.
        </Step>
        <Step n={2}>
          A sheet pops up from the bottom. <strong>Scroll down</strong> through the list of options until you see{' '}
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold mx-0.5 align-middle" style={{ backgroundColor: '#F3EFE7', border: '1px solid #E6E2D8' }}>
            <Plus className="w-3 h-3" style={{ color: '#5B7C99' }} />
            Add to Home Screen
          </span>
          . Tap it.
        </Step>
        <Step n={3}>
          Tap <strong>Add</strong> in the top-right. The Love Restored heart icon appears right on your home screen — tap it any time to open the site like an app.
        </Step>
      </ol>
      <div className="text-xs p-3 rounded-xl mt-3" style={{ backgroundColor: '#FBEFCE50', border: '1px solid #E8C26544', color: '#5C4515', fontFamily: 'Nunito, sans-serif', lineHeight: 1.6 }}>
        <strong>Can't find "Add to Home Screen"?</strong> This feature only exists in <strong>Safari</strong> on iPhone / iPad. If you're using Chrome or another browser, open <strong>loverestored.net</strong> in Safari first, then try again.
      </div>
    </div>
  );
}

function AndroidInstructions() {
  return (
    <div data-testid="install-android-steps">
      <p className="text-sm mb-3 flex items-center gap-2 font-semibold" style={{ fontFamily: 'Manrope, sans-serif', color: '#2C2A29' }}>
        <Smartphone className="w-4 h-4" style={{ color: '#B8891A' }} /> On Android (Chrome)
      </p>
      <ol className="space-y-0.5 mb-3">
        <Step n={1}>
          Tap the{' '}
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-md mx-0.5 align-middle" style={{ backgroundColor: '#F3EFE7', border: '1px solid #E6E2D8' }}>
            <MoreVertical className="w-3.5 h-3.5" style={{ color: '#5B7C99' }} />
          </span>{' '}
          <strong>three-dot menu</strong> in the top-right of Chrome.
        </Step>
        <Step n={2}>
          A menu opens. <strong>Scroll down</strong> and tap{' '}
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold mx-0.5 align-middle" style={{ backgroundColor: '#F3EFE7', border: '1px solid #E6E2D8' }}>
            <Download className="w-3 h-3" style={{ color: '#5B7C99' }} />
            Install app
          </span>{' '}
          (on older Chrome it may say "Add to Home screen" instead).
        </Step>
        <Step n={3}>
          Tap <strong>Install</strong>. Love Restored lands on your home screen like any other app — tap the heart icon to open it in its own window.
        </Step>
      </ol>
      <div className="text-xs p-3 rounded-xl mt-3" style={{ backgroundColor: '#FBEFCE50', border: '1px solid #E8C26544', color: '#5C4515', fontFamily: 'Nunito, sans-serif', lineHeight: 1.6 }}>
        <strong>Using Samsung Internet?</strong> Tap the bottom menu <strong>☰</strong>, then <strong>"Add page to" → "Home screen"</strong>. The same heart icon will show up.
      </div>
    </div>
  );
}

function DesktopInstructions() {
  return (
    <div data-testid="install-desktop-steps">
      <p className="text-sm mb-3 flex items-center gap-2 font-semibold" style={{ fontFamily: 'Manrope, sans-serif', color: '#2C2A29' }}>
        <Chrome className="w-4 h-4" style={{ color: '#B8891A' }} /> On your computer (Chrome / Edge)
      </p>
      <ol className="space-y-0.5 mb-3">
        <Step n={1}>
          Look to the <strong>right of the address bar</strong> for a small install icon{' '}
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-md mx-0.5 align-middle" style={{ backgroundColor: '#F3EFE7', border: '1px solid #E6E2D8' }}>
            <Download className="w-3.5 h-3.5" style={{ color: '#5B7C99' }} />
          </span>{' '}
          (or a <strong>⊕</strong> plus sign).
        </Step>
        <Step n={2}>
          Click it, then click <strong>Install</strong>.
        </Step>
        <Step n={3}>
          Love Restored opens in its own window and sits in your Dock, taskbar, or Start menu — just like any installed app.
        </Step>
      </ol>
      <div className="text-xs p-3 rounded-xl mt-3" style={{ backgroundColor: '#FBEFCE50', border: '1px solid #E8C26544', color: '#5C4515', fontFamily: 'Nunito, sans-serif', lineHeight: 1.6 }}>
        <strong>On Safari (Mac)?</strong> Click <strong>File → Add to Dock</strong>, then <strong>Add</strong>.
      </div>
    </div>
  );
}
